import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { get, put } from "src/utils/networkUtils";
import { getPositionsForSport } from "src/utils/scoreablesDashboardUtils";
import TeamPlayersTable from "src/components/live-scoring/TeamPlayersTable";
import MatchHeader from "src/components/live-scoring/MatchHeader";
import useWithUrlTenant from "src/hooks/useTenant";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import useTenantSportType from "src/hooks/useTenantSportType";
import { getTeamExternalId } from "src/utils/matchDetailsUtils";
import { buildManualScoringStats, getManualScoringConfig } from "src/utils/manualScoringConfig";

const MatchLiveScoringDialog = ({ open, onClose, onSaved, showMatch }) => {
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [homeScoreables, setHomeScoreables] = useState([]);
    const [awayScoreables, setAwayScoreables] = useState([]);
    const [editingStat, setEditingStat] = useState(null);
    const [editedStats, setEditedStats] = useState({});
    const [teamScoreableIds, setTeamScoreableIds] = useState({});
    const withTenant = useWithUrlTenant();
    const sportType = useTenantSportType();

    // Get tenant competitions to use the correct competition_id
    const { tenantCompetitions } = useTenantCompetitions();

    useEffect(() => {
        if (!open || !showMatch) return;

        const fetchScoreables = async () => {
            // Wait for tenant competitions to be loaded and ensure we have at least one
            if (!tenantCompetitions || tenantCompetitions.length === 0) {
                return;
            }

            // Use the first tenant competition's ID
            const competitionId = tenantCompetitions[0].competitionId;

            try {
                const response = await get(`admin/teams?competition_id=${competitionId}`);
                const teams = response.teams;

                if (!showMatch || !showMatch.details) return;

                const HomeTeamFeedId = getTeamExternalId(showMatch.details, 'Home');
                const AwayTeamFeedId = getTeamExternalId(showMatch.details, 'Away');

                const matchedTeams = teams.filter(
                    (team) =>
                        team.externalRef === HomeTeamFeedId ||
                        team.externalRef === AwayTeamFeedId
                );

                for (const team of matchedTeams) {
                    const scoreablesResponse = await get(
                        withTenant(
                            `admin/teams/${team.scoreableId}/scoreables?isInternational=false`
                        )
                    );

                    if (team.externalRef === HomeTeamFeedId) {
                        setTeamScoreableIds((prev) => ({ ...prev, home: team.scoreableId }));
                        setHomeScoreables(scoreablesResponse.scoreables || []);
                    } else if (team.externalRef === AwayTeamFeedId) {
                        setTeamScoreableIds((prev) => ({ ...prev, away: team.scoreableId }));
                        setAwayScoreables(scoreablesResponse.scoreables || []);
                    }
                }
            } catch (error) {
                console.error("Error fetching scoreables:", error);
            }
        };

        fetchScoreables();
    }, [open, showMatch, withTenant, tenantCompetitions]);


    if (!showMatch || !showMatch.details || !showMatch.stats) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Match Live Scoring
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="h6">No match data available</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const homeStats = showMatch.stats.find(
        (t) => t.TeamFeedId === showMatch.details.HomeTeamFeedId
    );
    const awayStats = showMatch.stats.find(
        (t) => t.TeamFeedId === showMatch.details.AwayTeamFeedId
    );

    const scoringConfig = getManualScoringConfig(sportType);
    const statKeys = scoringConfig.teamStats.map((stat) => stat.key);
    const positions = getPositionsForSport(sportType);

    const showRawData = false;

    const handleEditClick = (key, team) => {
        setEditingStat({ key, team });
        setEditedStats((prev) => ({
            ...prev,
            [`${team}-${key}`]:
                team === "home" ? homeStats?.[key] ?? 0 : awayStats?.[key] ?? 0,
        }));
    };

    const handleChange = (e, key, team) => {
        setEditedStats((prev) => ({
            ...prev,
            [`${team}-${key}`]: e.target.value,
        }));
    };

    const handleSave = async () => {
        try {
            const { stats, details } = buildManualScoringStats({
                sportType,
                stats: showMatch.stats,
                details: showMatch.details,
                editedStats,
                homeScoreables,
                awayScoreables,
                teamScoreableIds,
            });
            const payload = details ? { stats, details } : { stats };

            await put(withTenant(`admin/fixtures/${showMatch.fixtureId}`), payload);
            setEditingStat(null);
            setEditedStats({});
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error("Save error:", err);
        }
    };

    const sortData = (key, team) => {
        const direction =
            sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
        setSortConfig({ key, direction });

        const sortArray = (arr) => {
            return [...arr].sort((a, b) => {
                if (key === "displayName") {
                    return direction === "asc"
                        ? a.displayName.localeCompare(b.displayName)
                        : b.displayName.localeCompare(a.displayName);
                } else if (key === "position") {
                    const aLabel =
                        positions.find((pos) => pos.value === a.position)?.label ||
                        "";
                    const bLabel =
                        positions.find((pos) => pos.value === b.position)?.label ||
                        "";
                    return direction === "asc"
                        ? aLabel.localeCompare(bLabel)
                        : bLabel.localeCompare(aLabel);
                }
                return 0;
            });
        };

        if (team === "home") {
            setHomeScoreables(sortArray(homeScoreables));
        } else if (team === "away") {
            setAwayScoreables(sortArray(awayScoreables));
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { maxHeight: '90vh' } }}
        >
            <DialogTitle>
                Match Live Scoring
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                <Box
                    sx={{
                        p: 3,
                        bgcolor: "white",
                        borderRadius: "20px",
                        boxShadow: "0.5px 3px 10px 0px #7777771A",
                    }}
                >
                    <MatchHeader details={showMatch.details} status={showMatch.status} />

                    <Box sx={{ mt: 4 }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {statKeys.map((key, index) => {
                                const homeValue = homeStats?.[key] ?? 0;
                                const awayValue = awayStats?.[key] ?? 0;

                                return (
                                    <Box
                                        key={key}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-around",
                                            gap: 2,
                                            width: "90%",
                                            borderBottom:
                                                index === statKeys.length - 1 ? "none" : "2px solid #ccc",
                                            paddingBottom: 1,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {editingStat?.key === key && editingStat.team === "home" ? (
                                                <input
                                                    type="number"
                                                    value={
                                                        editedStats.hasOwnProperty(`home-${key}`)
                                                            ? editedStats[`home-${key}`]
                                                            : homeValue
                                                    }
                                                    onChange={(e) => handleChange(e, key, "home")}
                                                    onBlur={() => setEditingStat(null)}
                                                    style={{
                                                        width: 60,
                                                        padding: "4px 8px",
                                                        fontSize: "14px",
                                                        borderRadius: "4px",
                                                        border: "1px solid #ccc",
                                                    }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <>
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {editedStats.hasOwnProperty(`home-${key}`)
                                                            ? editedStats[`home-${key}`]
                                                            : homeValue}
                                                    </Typography>
                                                    <img
                                                        src="/assets/icons/EditIcon.svg"
                                                        alt="Edit"
                                                        style={{ width: 18, height: 18, cursor: "pointer" }}
                                                        onClick={() => handleEditClick(key, "home")}
                                                    />
                                                </>
                                            )}
                                        </Box>

                                        <Typography
                                            sx={{
                                                minWidth: 170,
                                                textAlign: "center",
                                                color: "#6A707E",
                                                fontSize: "16px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            {scoringConfig.teamStats[index].label}
                                        </Typography>

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {editingStat?.key === key && editingStat.team === "away" ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={
                                                        editedStats.hasOwnProperty(`away-${key}`)
                                                            ? editedStats[`away-${key}`]
                                                            : awayValue
                                                    }
                                                    onChange={(e) => handleChange(e, key, "away")}
                                                    onBlur={() => setEditingStat(null)}
                                                    sx={{ width: 60 }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {editedStats.hasOwnProperty(`away-${key}`)
                                                        ? editedStats[`away-${key}`]
                                                        : awayValue}
                                                </Typography>
                                            )}
                                            <img
                                                src="/assets/icons/EditIcon.svg"
                                                alt="Edit"
                                                style={{ width: 18, height: 18, cursor: "pointer" }}
                                                onClick={() => handleEditClick(key, "away")}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>

                <TeamPlayersTable
                    teamLabel="Home"
                    teamName={showMatch.details.HomeTeamName}
                    teamFeedId={getTeamExternalId(showMatch.details, 'Home')}
                    scoreables={homeScoreables}
                    teamStats={homeStats}
                    sortData={sortData}
                    editingStat={editingStat}
                    setEditingStat={setEditingStat}
                    editedStats={editedStats}
                    setEditedStats={setEditedStats}
                    sortKey="home"
                    playerStats={scoringConfig.playerStats}
                    positions={positions}
                />

                <TeamPlayersTable
                    teamLabel="Away"
                    teamName={showMatch.details.AwayTeamName}
                    teamFeedId={getTeamExternalId(showMatch.details, 'Away')}
                    scoreables={awayScoreables}
                    teamStats={awayStats}
                    sortData={sortData}
                    editingStat={editingStat}
                    setEditingStat={setEditingStat}
                    editedStats={editedStats}
                    setEditedStats={setEditedStats}
                    sortKey="away"
                    playerStats={scoringConfig.playerStats}
                    positions={positions}
                />

                {showRawData && (
                    <Collapse in={showRawData}>
                        <pre>{JSON.stringify(showMatch.stats, null, 2)}</pre>
                    </Collapse>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onClose}
                    sx={{ textTransform: "none" }}
                >
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MatchLiveScoringDialog;